/**
 * StoreMemoryAdmin Component
 * Admin interface for managing store memory notes across all stores
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  Brain,
  Plus,
  Edit2,
  Trash2,
  Search,
  RefreshCw,
  Store,
  Tag,
  Calendar,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { storeMemoryService } from '@/services/storeMemoryService'
import {
  StoreMemoryWithCreator,
  StoreMemoryCategory,
  STORE_MEMORY_CATEGORIES
} from '@shared/types'
import { cn } from '@/lib/utils'

const CATEGORY_LABELS: Record<StoreMemoryCategory, string> = {
  priorities: 'Priorities',
  issues: 'Issues',
  goals: 'Goals',
  general: 'General'
}

const CATEGORY_COLORS: Record<StoreMemoryCategory, string> = {
  priorities: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  issues: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  goals: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  general: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
}

interface GroupedMemories {
  [storeId: string]: {
    memories: StoreMemoryWithCreator[]
    isExpanded: boolean
  }
}

export const StoreMemoryAdmin: React.FC = () => {
  const [memories, setMemories] = useState<StoreMemoryWithCreator[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<StoreMemoryCategory | ''>('')
  const [storeFilter, setStoreFilter] = useState('')
  const [expandedStores, setExpandedStores] = useState<Set<string>>(new Set())

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')
  const [editCategory, setEditCategory] = useState<StoreMemoryCategory>('general')
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // Get unique store IDs
  const storeIds = [...new Set(memories.map(m => m.storeId))].sort()

  const fetchMemories = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const result = await storeMemoryService.listAll({
        category: categoryFilter || undefined,
        storeId: storeFilter || undefined,
        includeInactive: true,
        limit: 500
      })
      setMemories(result.memories)

      // Expand all stores by default if less than 5 stores
      if (result.memories.length > 0) {
        const stores = [...new Set(result.memories.map(m => m.storeId))]
        if (stores.length <= 5) {
          setExpandedStores(new Set(stores))
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch store memories'
      setError(errorMessage)
      console.error('Failed to fetch store memories:', err)
    } finally {
      setLoading(false)
    }
  }, [categoryFilter, storeFilter])

  useEffect(() => {
    fetchMemories()
  }, [fetchMemories])

  // Filter memories by search term
  const filteredMemories = memories.filter(memory => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      memory.content.toLowerCase().includes(search) ||
      memory.storeId.toLowerCase().includes(search) ||
      memory.creator.firstName.toLowerCase().includes(search) ||
      memory.creator.lastName.toLowerCase().includes(search)
    )
  })

  // Group memories by store
  const groupedMemories: GroupedMemories = filteredMemories.reduce((acc, memory) => {
    if (!acc[memory.storeId]) {
      acc[memory.storeId] = {
        memories: [],
        isExpanded: expandedStores.has(memory.storeId)
      }
    }
    acc[memory.storeId].memories.push(memory)
    return acc
  }, {} as GroupedMemories)

  const toggleStore = (storeId: string) => {
    setExpandedStores(prev => {
      const next = new Set(prev)
      if (next.has(storeId)) {
        next.delete(storeId)
      } else {
        next.add(storeId)
      }
      return next
    })
  }

  const handleEdit = async (id: string) => {
    if (!editContent.trim()) return

    setActionLoading(true)
    setActionError(null)

    try {
      const updated = await storeMemoryService.update(id, {
        content: editContent.trim(),
        category: editCategory
      })
      setMemories(prev => prev.map(m => m.id === id ? updated : m))
      setEditingId(null)
      setEditContent('')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update note')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this note?')) return

    setActionLoading(true)
    setActionError(null)

    try {
      await storeMemoryService.delete(id)
      setMemories(prev => prev.filter(m => m.id !== id))
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete note')
    } finally {
      setActionLoading(false)
    }
  }

  const startEditing = (memory: StoreMemoryWithCreator) => {
    setEditingId(memory.id)
    setEditContent(memory.content)
    setEditCategory(memory.category as StoreMemoryCategory || 'general')
    setActionError(null)
  }

  const cancelEditing = () => {
    setEditingId(null)
    setEditContent('')
    setEditCategory('general')
    setActionError(null)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-foreground">Store Memory Management</h3>
          <p className="text-sm text-muted-foreground mt-1">
            View and manage persistent notes for all store locations
          </p>
        </div>
        <button
          onClick={fetchMemories}
          disabled={loading}
          className="inline-flex items-center px-3 py-2 border rounded-md text-sm hover:bg-muted transition-colors disabled:opacity-50"
        >
          <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search notes..."
            className="w-full pl-10 pr-4 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Store Filter */}
        <select
          value={storeFilter}
          onChange={(e) => setStoreFilter(e.target.value)}
          className="px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">All Stores</option>
          {storeIds.map(id => (
            <option key={id} value={id}>Store {id}</option>
          ))}
        </select>

        {/* Category Filter */}
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as StoreMemoryCategory | '')}
          className="px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">All Categories</option>
          {STORE_MEMORY_CATEGORIES.map(cat => (
            <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="text-2xl font-bold">{filteredMemories.length}</div>
          <div className="text-sm text-muted-foreground">Total Notes</div>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="text-2xl font-bold">{Object.keys(groupedMemories).length}</div>
          <div className="text-sm text-muted-foreground">Stores with Notes</div>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="text-2xl font-bold">
            {filteredMemories.filter(m => m.isActive).length}
          </div>
          <div className="text-sm text-muted-foreground">Active Notes</div>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="text-2xl font-bold">
            {filteredMemories.filter(m => !m.isActive).length}
          </div>
          <div className="text-sm text-muted-foreground">Archived Notes</div>
        </div>
      </div>

      {/* Error Display */}
      {(error || actionError) && (
        <div className="flex items-center space-x-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{actionError || error}</span>
        </div>
      )}

      {/* Notes by Store */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : Object.keys(groupedMemories).length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Brain className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No store memory notes found</p>
          {(searchTerm || categoryFilter || storeFilter) && (
            <p className="text-sm mt-2">Try adjusting your filters</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedMemories)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([storeId, { memories: storeMemories }]) => (
              <div key={storeId} className="border rounded-lg overflow-hidden">
                {/* Store Header */}
                <button
                  onClick={() => toggleStore(storeId)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Store className="w-5 h-5 text-primary" />
                    <span className="font-medium">Store {storeId}</span>
                    <span className="text-sm text-muted-foreground">
                      ({storeMemories.length} note{storeMemories.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  {expandedStores.has(storeId) ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>

                {/* Store Notes */}
                {expandedStores.has(storeId) && (
                  <div className="p-4 space-y-3">
                    {storeMemories.map(memory => (
                      <div
                        key={memory.id}
                        className={cn(
                          "p-4 rounded-lg border",
                          !memory.isActive && "opacity-60 bg-muted/30",
                          editingId === memory.id && "ring-2 ring-primary"
                        )}
                      >
                        {editingId === memory.id ? (
                          // Edit Mode
                          <div className="space-y-3">
                            <textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="w-full px-3 py-2 text-sm bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                              rows={3}
                              autoFocus
                            />
                            <div className="flex items-center gap-3">
                              <select
                                value={editCategory}
                                onChange={(e) => setEditCategory(e.target.value as StoreMemoryCategory)}
                                className="px-2 py-1.5 text-sm bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                              >
                                {STORE_MEMORY_CATEGORIES.map(cat => (
                                  <option key={cat} value={cat}>
                                    {CATEGORY_LABELS[cat]}
                                  </option>
                                ))}
                              </select>
                              <div className="flex-1" />
                              <button
                                onClick={cancelEditing}
                                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                                disabled={actionLoading}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleEdit(memory.id)}
                                disabled={!editContent.trim() || actionLoading}
                                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                              >
                                {actionLoading ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  'Save'
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          // Display Mode
                          <>
                            <div className="flex items-start justify-between gap-3">
                              <p className="text-sm flex-1">{memory.content}</p>
                              <div className="flex items-center space-x-1 flex-shrink-0">
                                <button
                                  onClick={() => startEditing(memory)}
                                  className="p-1.5 text-muted-foreground hover:text-foreground rounded hover:bg-muted"
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(memory.id)}
                                  className="p-1.5 text-muted-foreground hover:text-destructive rounded hover:bg-muted"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
                              {memory.category && (
                                <span className={cn(
                                  "inline-flex items-center space-x-1 px-2 py-0.5 rounded",
                                  CATEGORY_COLORS[memory.category as StoreMemoryCategory]
                                )}>
                                  <Tag className="w-3 h-3" />
                                  <span>{CATEGORY_LABELS[memory.category as StoreMemoryCategory]}</span>
                                </span>
                              )}
                              {!memory.isActive && (
                                <span className="px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                  Archived
                                </span>
                              )}
                              <span className="flex items-center space-x-1">
                                <Calendar className="w-3 h-3" />
                                <span>{formatDate(memory.createdAt)}</span>
                              </span>
                              <span>
                                by {memory.creator.firstName} {memory.creator.lastName}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  )
}

export default StoreMemoryAdmin
